import type { Graphics, Rectangle } from "pixi.js";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useImageOverlayInteractions } from "../hooks/useImageOverlayInteractions";
import { useLayerCulling } from "../hooks/useLayerCulling";
import { useScene } from "../hooks/useScene";
import type { ImageOverlayGeometry } from "../imageOverlayGeometry";
import {
    getImageOverlayCenter,
    projectImageOverlayToScene,
} from "../imageOverlayGeometry";
import ImageOverlayObject from "../objects/ImageOverlayObject";
import type { ImageOverlayMapObject, MapLayerProps } from "../types";

interface ImageOverlayRenderGeometry extends ImageOverlayGeometry {
    cullBounds: Rectangle;
}

type ImageOverlaysLayerProps = Pick<
    MapLayerProps<ImageOverlayMapObject>,
    "id" | "items" | "selectedIds" | "onItemClick"
>;

const EMPTY_ITEMS: readonly ImageOverlayMapObject[] = [];

const ForegroundOutline = ({
    item,
    zIndex,
}: {
    item: ImageOverlayMapObject;
    zIndex: number;
}) => {
    const { coordinates } = useScene();
    const sceneGeometry = useMemo(
        () => projectImageOverlayToScene(item.corners, coordinates),
        [coordinates, item.corners],
    );
    const draw = useCallback(
        (graphics: Graphics) => {
            if (!sceneGeometry) return;

            const [topLeft, topRight, bottomRight, bottomLeft] =
                sceneGeometry.sceneCorners;

            graphics.clear();
            graphics
                .moveTo(topLeft.x, topLeft.y)
                .lineTo(topRight.x, topRight.y)
                .lineTo(bottomRight.x, bottomRight.y)
                .lineTo(bottomLeft.x, bottomLeft.y)
                .closePath()
                .stroke({ color: 0xffffff, width: 1, pixelLine: true });
        },
        [sceneGeometry],
    );

    if (!sceneGeometry) return null;

    return (
        <pixiContainer
            label="image-overlay-foreground-outline"
            cullable
            cullableChildren={false}
            cullArea={sceneGeometry.cullBounds}
            zIndex={zIndex}
            eventMode="none"
        >
            <pixiGraphics eventMode="none" draw={draw} />
        </pixiContainer>
    );
};

interface ImageOverlayInteractionLayerProps {
    id: string;
    items: readonly ImageOverlayMapObject[];
    onItemClick?: ImageOverlaysLayerProps["onItemClick"];
    onHoveredIdChange: (hoveredId: string | null) => void;
}

const ImageOverlayInteractionLayer = ({
    id,
    items,
    onItemClick,
    onHoveredIdChange,
}: ImageOverlayInteractionLayerProps) => {
    const { coordinates } = useScene();
    const geometries = useMemo(
        () =>
            items.flatMap((item): ImageOverlayRenderGeometry[] => {
                const sceneGeometry = projectImageOverlayToScene(
                    item.corners,
                    coordinates,
                );
                if (!sceneGeometry) return [];

                return [
                    {
                        item,
                        worldCorners: item.corners,
                        worldCenter: getImageOverlayCenter(item.corners),
                        ...sceneGeometry,
                    },
                ];
            }),
        [coordinates, items],
    );
    useLayerCulling(geometries);
    const { hoveredId, surfaceProps } = useImageOverlayInteractions({
        id,
        geometries,
        onItemClick,
    });

    useEffect(() => {
        onHoveredIdChange(hoveredId);
    }, [hoveredId, onHoveredIdChange]);

    return (
        <pixiContainer
            label="image-overlays-interaction-layer"
            zIndex={1}
            eventMode="static"
            interactiveChildren={false}
            {...surfaceProps}
        />
    );
};

// Image source changes do not invalidate projected geometry or hit testing.
const haveSameInteractionGeometry = (
    previous: ImageOverlayInteractionLayerProps,
    next: ImageOverlayInteractionLayerProps,
): boolean => {
    if (
        previous.id !== next.id ||
        previous.onItemClick !== next.onItemClick ||
        previous.onHoveredIdChange !== next.onHoveredIdChange ||
        previous.items.length !== next.items.length
    ) {
        return false;
    }

    return previous.items.every((previousItem, index) => {
        const nextItem = next.items[index];
        return (
            previousItem.id === nextItem.id &&
            previousItem.corners === nextItem.corners &&
            previousItem.interactive === nextItem.interactive
        );
    });
};

const MemoizedImageOverlayInteractionLayer = memo(
    ImageOverlayInteractionLayer,
    haveSameInteractionGeometry,
);

const ImageOverlaysLayer = ({
    id,
    items,
    selectedIds,
    onItemClick,
}: ImageOverlaysLayerProps) => {
    const layerItems = items ?? EMPTY_ITEMS;
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    const selectedItem = useMemo(
        () => layerItems.find((item) => selectedIds?.has(item.id)),
        [layerItems, selectedIds],
    );
    const hoveredItem = useMemo(
        () =>
            layerItems.find(
                (item) => item.interactive !== false && item.id === hoveredId,
            ),
        [hoveredId, layerItems],
    );
    const foregroundItem = selectedItem ?? hoveredItem;

    return (
        <>
            <pixiContainer
                label="image-overlays-layer"
                sortableChildren
                eventMode="none"
            >
                {layerItems.map((item, order) => (
                    <ImageOverlayObject
                        key={item.id}
                        item={item}
                        selected={selectedIds?.has(item.id) ?? false}
                        foreground={foregroundItem?.id === item.id}
                        order={order}
                        layerItemCount={layerItems.length}
                    />
                ))}
                {foregroundItem && (
                    <ForegroundOutline
                        item={foregroundItem}
                        zIndex={layerItems.length * 2 + 1}
                    />
                )}
            </pixiContainer>
            <MemoizedImageOverlayInteractionLayer
                id={id}
                items={layerItems}
                onItemClick={onItemClick}
                onHoveredIdChange={setHoveredId}
            />
        </>
    );
};

export default ImageOverlaysLayer;
